import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Search, ArrowLeft, Trash2, Eye, EyeOff, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAdminBands, adminDeleteBand, adminUpdateBand } from '../../hooks/useAdmin';
import { formatDate, getInitials } from '../../lib/utils';

export default function AdminBandsPage() {
  const { bands, loading, pagination, fetchBands } = useAdminBands();
  const [search, setSearch] = useState('');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  function handleSearch(e: FormEvent) {
    e.preventDefault();
    fetchBands(search, 0);
  }

  function goToPage(page: number) {
    fetchBands(search, page);
  }

  function showMessage(type: 'success' | 'error', text: string) {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 3000);
  }

  async function handleToggleVisibility(bandId: string, currentlyPublic: boolean, name: string) {
    const { error } = await adminUpdateBand(bandId, { is_public: !currentlyPublic });
    if (error) {
      showMessage('error', error);
    } else {
      showMessage('success', `${name} is now ${currentlyPublic ? 'private' : 'public'}`);
      fetchBands(search, pagination.page);
    }
  }

  async function handleDelete(bandId: string, name: string) {
    if (!confirm(`Are you sure you want to permanently delete "${name}" and all its events, setlists, and members? This cannot be undone.`)) return;

    const { error } = await adminDeleteBand(bandId);
    if (error) {
      showMessage('error', error);
    } else {
      showMessage('success', `Deleted "${name}"`);
      fetchBands(search, pagination.page);
    }
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <div className="flex items-center gap-3 mb-6">
        <Link to="/admin" className="text-gray-400 hover:text-gray-50 transition">
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <h1 className="text-2xl font-bold">Manage Bands</h1>
      </div>

      {message && (
        <div className={`mb-4 px-4 py-3 rounded-lg text-sm ${
          message.type === 'success' ? 'bg-green-900/50 border border-green-700 text-green-200' : 'bg-red-900/50 border border-red-700 text-red-200'
        }`}>
          {message.text}
        </div>
      )}

      <form onSubmit={handleSearch} className="mb-6">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, location, or slug..."
              className="w-full bg-gray-800 border border-gray-700 rounded-lg pl-10 pr-4 py-2 text-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <button type="submit" className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition">
            Search
          </button>
        </div>
      </form>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-400"></div>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm text-gray-400">
              {pagination.totalCount} band{pagination.totalCount !== 1 ? 's' : ''}
              {pagination.totalPages > 1 && ` — page ${pagination.page + 1} of ${pagination.totalPages}`}
            </p>
          </div>
          {bands.map((band) => (
            <div key={band.id} className="bg-gray-900 border border-gray-800 rounded-xl p-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  {band.logo_url ? (
                    <img src={band.logo_url} alt={band.name} className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-10 h-10 rounded-lg bg-gray-800 flex items-center justify-center flex-shrink-0">
                      <span className="text-gray-400 font-bold text-sm">{getInitials(band.name)}</span>
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium truncate">{band.name}</span>
                      {band.is_public ? (
                        <span className="bg-green-900/50 text-green-300 text-xs px-2 py-0.5 rounded-full flex-shrink-0">public</span>
                      ) : (
                        <span className="bg-yellow-900/50 text-yellow-300 text-xs px-2 py-0.5 rounded-full flex-shrink-0">private</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-400 truncate">
                      /{band.slug}
                      {band.location_name && ` | ${band.location_name}`}
                      {band.location_country && `, ${band.location_country}`}
                    </p>
                    <p className="text-xs text-gray-600 mt-0.5">Created {formatDate(band.created_at)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <Link
                    to={`/band/${band.slug}`}
                    className="text-gray-400 hover:text-indigo-400 px-2 py-1.5 rounded-lg hover:bg-gray-800 transition"
                    title="View public page"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </Link>
                  <Link
                    to={`/band/${band.id}/manage`}
                    className="text-gray-400 hover:text-gray-50 px-2 py-1.5 rounded-lg hover:bg-gray-800 transition text-xs"
                  >
                    Manage
                  </Link>
                  <button
                    onClick={() => handleToggleVisibility(band.id, band.is_public, band.name)}
                    className="text-gray-400 hover:text-amber-400 px-2 py-1.5 rounded-lg hover:bg-gray-800 transition"
                    title={band.is_public ? 'Make private' : 'Make public'}
                  >
                    {band.is_public ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={() => handleDelete(band.id, band.name)}
                    className="text-gray-400 hover:text-red-400 px-2 py-1.5 rounded-lg hover:bg-gray-800 transition"
                    title="Delete band"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}

          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 pt-4">
              <button
                onClick={() => goToPage(pagination.page - 1)}
                disabled={pagination.page === 0}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition disabled:opacity-30 disabled:cursor-not-allowed text-gray-300 hover:bg-gray-800"
              >
                <ChevronLeft className="w-4 h-4" /> Prev
              </button>
              {Array.from({ length: pagination.totalPages }, (_, i) => i)
                .filter((i) => i === 0 || i === pagination.totalPages - 1 || Math.abs(i - pagination.page) <= 1)
                .reduce<(number | 'ellipsis')[]>((acc, i, idx, arr) => {
                  if (idx > 0 && arr[idx - 1] !== i - 1) acc.push('ellipsis');
                  acc.push(i);
                  return acc;
                }, [])
                .map((item, idx) =>
                  item === 'ellipsis' ? (
                    <span key={`e${idx}`} className="text-gray-500 px-1">...</span>
                  ) : (
                    <button
                      key={item}
                      onClick={() => goToPage(item)}
                      className={`w-8 h-8 rounded-lg text-sm font-medium transition ${
                        item === pagination.page
                          ? 'bg-indigo-600 text-white'
                          : 'text-gray-400 hover:bg-gray-800 hover:text-gray-50'
                      }`}
                    >
                      {item + 1}
                    </button>
                  )
                )}
              <button
                onClick={() => goToPage(pagination.page + 1)}
                disabled={pagination.page >= pagination.totalPages - 1}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition disabled:opacity-30 disabled:cursor-not-allowed text-gray-300 hover:bg-gray-800"
              >
                Next <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
